import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { AlertCircle, Plus, Edit3, Trash2, X, Search, LayoutGrid, CheckCircle, AlertTriangle, TrendingUp, MapPin, Box, Printer, ChevronDown, ChevronRight, Eye, Camera, Loader } from 'lucide-react';
import FramePhotoPicker from '../../components/FramePhotoPicker/FramePhotoPicker';
import CameraCaptureModal, { cameraAvailable } from '../../components/FramePhotoPicker/CameraCaptureModal';
import { downscaleImage } from '../../utils/imageResize';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import { hasTask } from '../../utils/tasks';
import { advisorAccessFor } from '../../utils/advisor';
import { createFrameProduct, deleteFrameProduct, getFrameProducts, getFrameStock, returnFrameToCentral, setFrameProductImage, updateFrameProduct, updateFrameStock } from '../../services/frameStockService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-bottom: 2rem;
  
  @media print {
    display: none;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.4rem;
  border-radius: 24px;
  background:
    radial-gradient(circle at top right, rgba(99, 102, 241, 0.18), transparent 34%),
    linear-gradient(135deg, #ffffff 0%, #f8faff 52%, #eefdf8 100%);
  border: 1px solid #e5eaf2;
  box-shadow: 0 18px 50px -34px rgba(15, 23, 42, 0.45);

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const TitleColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const PageEyebrow = styled.div`
  width: fit-content;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  border: 1px solid #dbe3ff;
  padding: 0.32rem 0.62rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const PageTitle = styled.h1`
  font-size: clamp(1.75rem, 2.6vw, 2.35rem);
  font-weight: 850;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.04em;
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 640px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 760px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 0.9rem;
`;

const StatCard = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border-radius: 20px;
  padding: 1.15rem 1.2rem;
  display: flex;
  align-items: center;
  gap: 1.1rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 30px -24px rgba(15, 23, 42, 0.5);
  border: 1px solid #e8eef7;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.24s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.24s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${props => props.accent || 'linear-gradient(90deg, #6366f1 0%, #a855f7 100%)'};
    opacity: 0.9;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px -8px rgba(15, 23, 42, 0.12), 0 4px 8px -4px rgba(30, 41, 99, 0.07);
    border-color: #dbe2ee;
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg || '#f1f5f9'};
  color: ${props => props.color || '#64748b'};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55),
              inset 0 1px 1px rgba(255, 255, 255, 0.7),
              0 2px 6px -1px rgba(15, 23, 42, 0.10);
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  ${StatCard}:hover & {
    transform: scale(1.06);
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const StatLabel = styled.span`
  font-size: 0.74rem;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StatValue = styled.span`
  font-size: 1.7rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
`;

const FilterBar = styled.div`
  background: rgba(255, 255, 255, 0.88);
  border-radius: 20px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  box-shadow: 0 14px 36px -30px rgba(15, 23, 42, 0.55);
  border: 1px solid #e8eef7;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  @media (max-width: 720px) {
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 280px;

  @media (max-width: 720px) {
    min-width: 100%;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  width: 18px;
  height: 18px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.82rem 1rem 0.82rem 2.5rem;
  border-radius: 14px;
  border: 1px solid #dbe3ef;
  background: #ffffff;
  font-size: 0.95rem;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  color: #334155;

  &:focus {
    outline: none;
    background: white;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SelectGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 720px) {
    width: 100%;
  }
`;

const StyledSelect = styled.select`
  padding: 0.72rem 2rem 0.72rem 0.85rem;
  border-radius: 14px;
  border: 1px solid #dbe3ef;
  background-color: white;
  color: #334155;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: #6366f1;
  }

  @media (max-width: 720px) {
    flex: 1;
    min-width: 150px;
  }
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'danger' ? '#fff1f2' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'};
  color: ${props => props.variant === 'danger' ? '#be123c' : 'white'};
  border: ${props => props.variant === 'danger' ? '1px solid #fecdd3' : 'none'};
  border-radius: 14px;
  padding: 0.78rem 1.1rem;
  font-weight: 750;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.18s ease, background 0.18s ease;
  box-shadow: ${props => props.variant === 'danger' ? 'none' : '0 12px 22px -14px rgba(79, 70, 229, 0.75)'};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 8px -1px rgba(99, 102, 241, 0.3);
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 18px 48px -34px rgba(15, 23, 42, 0.45);
  border: 1px solid #e5eaf2;
`;

const TableToolbar = styled.div`
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const TableTitle = styled.div`
  display: grid;
  gap: 0.15rem;

  strong {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 800;
  }

  span {
    color: #64748b;
    font-size: 0.82rem;
  }
`;

const StockTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 0.95rem 1.25rem;
    text-align: left;
    border-bottom: 1px solid #f1f5f9;
  }

  th {
    background: #fbfdff;
    color: #475569;
    font-weight: 800;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  tr:hover {
    background: #fbfdff;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const BrandGroupCell = styled.td`
  padding: 0 !important;
  background: linear-gradient(135deg, #f8faff 0%, #eff6ff 52%, #f8fafc 100%);
  border-bottom: 1px solid #dbe3ef !important;
`;

const BrandToggle = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0.95rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  cursor: pointer;
  text-align: left;
  color: #0f172a;
  transition: background 0.16s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.07);
  }

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const BrandTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;

  strong {
    font-size: 1rem;
    font-weight: 800;
    color: #1e293b;
  }
`;

const BrandMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
`;

const BrandCountPill = styled.span`
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  color: #4f46e5;
  padding: 0.28rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 800;
`;

const FrameNameStack = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const FrameBrandBadge = styled.span`
  width: fit-content;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.45rem;
`;

const StatusBadge = styled.span`
  padding: 0.42rem 0.78rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  
  ${props => props.status === 'instock' && `
    background: #dcfce7;
    color: #166534;
    box-shadow: inset 0 0 0 1px rgba(22, 101, 52, 0.08);
  `}
  
  ${props => props.status === 'low' && `
    background: #fef9c3;
    color: #854d0e;
    box-shadow: inset 0 0 0 1px rgba(133, 77, 14, 0.08);
  `}
  
  ${props => props.status === 'out' && `
    background: #fee2e2;
    color: #991b1b;
    box-shadow: inset 0 0 0 1px rgba(153, 27, 27, 0.08);
  `}
`;

const Chip = styled.span`
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  color: #475569;
  font-size: 0.76rem;
  font-weight: 750;
`;

const ColorDot = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${props => props.color || '#f8fafc'};
  border: 2px solid white;
  box-shadow: 0 0 0 1px #e2e8f0;
  display: inline-block;
  margin-right: -6px;
  position: relative;
  
  &:hover {
    z-index: 10;
    transform: scale(1.1);
  }
`;

const QtyDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-weight: 850;
  color: #0f172a;
  font-size: 0.95rem;
  font-variant-numeric: tabular-nums;
`;

const QtyPercent = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
`;

const NoticeOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2200;
`;

const NoticeModal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 30px -15px rgba(0, 0, 0, 0.3);
`;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const NoticeTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
`;

const NoticeMessage = styled.p`
  margin: 0 0 1.25rem 0;
  color: #475569;
  line-height: 1.5;
`;

const ReturnModalCard = styled.div`
  background: white;
  border-radius: 22px;
  width: min(520px, calc(100vw - 2rem));
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
`;

const ReturnModalHeader = styled.div`
  padding: 1.25rem 1.35rem;
  background: linear-gradient(135deg, #0f172a 0%, #075985 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;

  h3 {
    margin: 0;
    font-size: 1.12rem;
    font-weight: 800;
  }

  p {
    margin: 0.3rem 0 0;
    color: #cbd5e1;
    line-height: 1.45;
    font-size: 0.88rem;
  }
`;

const CloseModalButton = styled.button`
  width: 38px;
  height: 38px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const ReturnModalBody = styled.div`
  padding: 1.25rem 1.35rem;
  display: grid;
  gap: 1rem;
`;

const ReturnSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryBox = styled.div`
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 14px;
  padding: 0.8rem;

  span {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  strong {
    display: block;
    margin-top: 0.25rem;
    color: #0f172a;
    font-size: 1rem;
  }
`;

const QuantityField = styled.div`
  display: grid;
  gap: 0.45rem;

  label {
    color: #334155;
    font-weight: 700;
    font-size: 0.9rem;
  }

  input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #cbd5e1;
    border-radius: 14px;
    padding: 0.85rem 0.95rem;
    font-size: 1rem;
    color: #0f172a;
    background: white;
    transition: all 0.18s ease;
  }

  input:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.14);
  }

  small {
    color: #64748b;
    line-height: 1.45;
  }
`;

const ReturnActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const IconButton = styled.button`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.16s ease, color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    background: #f8fafc;
    color: #6366f1;
    border-color: #c7d2fe;
    box-shadow: 0 8px 18px -14px rgba(79, 70, 229, 0.8);
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.96);
  }
`;

const LoadingState = styled.div`
  padding: 3.5rem 1.5rem;
  text-align: center;
  color: #64748b;
  display: grid;
  place-items: center;
  gap: 0.75rem;
`;

const LoadingSpinner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 3px solid #e0e7ff;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  padding: 3.5rem 1.5rem;
  text-align: center;
  color: #64748b;
  display: grid;
  place-items: center;
  gap: 0.65rem;

  strong {
    color: #0f172a;
    font-size: 1.05rem;
  }
`;

const DirtyBar = styled.div`
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #eef2f7;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: linear-gradient(135deg, #f8faff 0%, #eef2ff 100%);
  flex-wrap: wrap;
`;

const DirtyHint = styled.span`
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
`;

// Styles for the Print Modal
const PrintModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3200;
  padding: 1rem;
  overflow: auto;
  
  @media print {
    background: transparent;
    display: flex;
  }
`;

const PrintPreview = styled.div`
  background: white;
  padding: 1.1rem;
  border-radius: 18px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  width: 300px;
  max-width: calc(100vw - 2rem);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.32);
  border: 1px solid rgba(226, 232, 240, 0.9);

  @media print {
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;

    & > *:not(.print-label) {
      display: none;
    }
  }
`;

// On-screen preview is rendered at the real label size (40mm × 20mm) so what
// you see matches what prints. The dedicated print window handles the actual job.
const PrintableLabel = styled.div`
  width: 40mm;
  height: 20mm;
  padding: 1.5mm;
  box-sizing: border-box;
  overflow: hidden;
  margin: 0 auto;
  background: white;
  border: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;

  .label-inner {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 1.5mm;
    width: 100%;
  }

  .qr-section {
    width: 15mm;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .qr-section svg,
  .qr-section img,
  .qr-section canvas {
    width: 14mm;
    height: 14mm;
    display: block;
  }

  .text-section {
    flex: 1;
    height: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    text-align: left;
  }

  .label-title {
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .label-code,
  .label-price {
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .label-price {
    font-weight: 600;
    margin-top: 0.4mm;
  }
`;

const ControlsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  align-items: end;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: #475569;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.95rem;
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder { color: #94a3b8; }

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }

  &[type="color"] {
    height: 42px;
    padding: 0.2rem;
    cursor: pointer;
    width: 100%;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0;

  & + & { border-top: 1px solid #eef2f7; }

  span.lbl { font-weight: 600; color: #334155; font-size: 0.9rem; }
  span.hint { font-size: 0.78rem; color: ${props => (props.on ? '#64748b' : '#6366f1')}; }
`;

const Switch = styled.button`
  flex-shrink: 0;
  width: 46px;
  height: 26px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  position: relative;
  background: ${props => (props.on ? '#6366f1' : '#cbd5e1')};
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => (props.on ? '23px' : '3px')};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3);
    transition: left 0.2s ease;
  }
`;

const RemoveColorBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: none;
  border: none;
  color: #ef4444;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover { color: #dc2626; text-decoration: underline; }
`;

const ColorPickerPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 0.65rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
`;

const ColorPickerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0.65rem;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
`;

const ColorPreview = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 9px;
  border: 1px solid ${props => props.empty ? '#cbd5e1' : 'rgba(15, 23, 42, 0.16)'};
  background: ${props => props.empty ? '#ffffff' : props.fill};
  box-shadow: ${props => props.empty ? 'none' : 'inset 0 1px 4px rgba(255, 255, 255, 0.55), 0 2px 6px rgba(15, 23, 42, 0.08)'};
  position: relative;
  flex-shrink: 0;

  &::after {
    content: ${props => props.empty ? "'+'" : "''"};
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1rem;
    font-weight: 700;
  }
`;

const ColorSwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 0.45rem;
`;

const ColorSwatchButton = styled.button`
  min-height: 52px;
  border-radius: 11px;
  border: 1.5px solid ${props => props.selected ? '#6366f1' : '#e2e8f0'};
  background: ${props => props.selected ? '#eef2ff' : '#ffffff'};
  color: ${props => props.selected ? '#4338ca' : '#475569'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  transition: transform 0.14s ease, border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;

  &:hover {
    border-color: #a5b4fc;
    box-shadow: 0 6px 16px -10px rgba(79, 70, 229, 0.5);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.16);
  }
`;

const PaletteColorDot = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: ${props => props.fill};
  border: 1px solid rgba(15, 23, 42, 0.15);
  box-shadow: inset 0 1px 4px rgba(255, 255, 255, 0.55), 0 2px 5px rgba(15, 23, 42, 0.08);
`;

const CustomColorLabel = styled.label`
  position: relative;
  margin-top: 0.55rem;
  min-height: 38px;
  border-radius: 10px;
  border: 1px dashed #c7d2fe;
  background: #f8fafc;
  color: #4f46e5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  transition: background 0.16s ease, border-color 0.16s ease;

  &:hover {
    background: #eef2ff;
    border-color: #818cf8;
  }
`;

const HiddenColorInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
`;

const FormSectionTitle = styled.h3`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6366f1;
  margin: 0 0 0.85rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #eef2ff;
`;

const Button = styled.button`
  padding: 0.62rem 1.15rem;
  border-radius: 10px;
  border: ${props => props.variant === 'secondary' ? '1px solid #e2e8f0' : 'none'};
  background: ${props => props.variant === 'secondary' ? '#f8fafc' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'};
  color: ${props => props.variant === 'secondary' ? '#1e293b' : 'white'};
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, opacity 0.2s ease;
  box-shadow: ${props => props.variant === 'secondary' ? 'none' : '0 4px 12px -4px rgba(79, 70, 229, 0.5)'};

  &:hover {
    transform: translateY(-1px);
    ${props => props.variant === 'secondary'
      ? 'background: #eef2f7; border-color: #cbd5e1;'
      : 'box-shadow: 0 7px 18px -5px rgba(79, 70, 229, 0.6);'}
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const modalFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalPop = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  animation: ${modalFade} 0.18s ease;

  @media (max-width: 640px) {
    padding: 0.75rem;
    align-items: flex-end;
  }
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 940px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 64px -16px rgba(15, 23, 42, 0.35), 0 8px 24px -12px rgba(15, 23, 42, 0.18);
  animation: ${modalPop} 0.22s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.35rem 1.75rem 1.15rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  flex-shrink: 0;
`;

const ModalKicker = styled.div`
  color: #6366f1;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
`;

const ModalTitle = styled.h2`
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 0.4rem 0 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
`;

const CloseButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const ModalBody = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  background: #f8fafc;
`;

const FormSectionCard = styled.section`
  background: #ffffff;
  border: 1px solid #e5eaf2;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);

  & + & {
    margin-top: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
`;

const SectionDescription = styled.p`
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
  margin: -0.55rem 0 0;
`;

const RequiredPill = styled.span`
  flex-shrink: 0;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0.35rem 0.55rem;
`;

const FriendlyNotice = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 0.85rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.85rem;
  line-height: 1.45;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
`;

const SameForAllPanel = styled.div`
  border: 1px solid #dbeafe;
  border-radius: 14px;
  padding: 0.6rem 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f8fbff 0%, #eef2ff 100%);
`;

const PerFrameList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const PerFrameCard = styled.div`
  padding: 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
`;

const CheckboxLine = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.55rem;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.35;

  & + & {
    margin-top: 0.5rem;
  }

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #6366f1;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.75rem;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  flex-shrink: 0;
  border-radius: 0 0 16px 16px;
`;

const FooterHint = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const OptionCard = styled.div`
  padding: 0.6rem 0.5rem;
  border: 1.5px solid ${props => props.selected ? '#6366f1' : '#e2e8f0'};
  background: ${props => props.selected ? '#eef2ff' : 'white'};
  color: ${props => props.selected ? '#4338ca' : '#64748b'};
  border-radius: 10px;
  cursor: pointer;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 58px;
  gap: 0.35rem;
  user-select: none;

  &:hover {
    border-color: ${props => props.selected ? '#6366f1' : '#c7d2fe'};
    background: ${props => props.selected ? '#eef2ff' : '#f8fafc'};
  }

  &:active {
    transform: scale(0.97);
  }
`;

const IconImage = styled.img`
  width: 44px;
  height: 22px;
  object-fit: contain;
  opacity: 0.9;
  filter: ${props => props.selected ? 'brightness(0) saturate(100%) invert(23%) sepia(58%) saturate(2465%) hue-rotate(228deg) brightness(85%) contrast(85%)' : 'grayscale(100%) opacity(0.6)'};
  transition: all 0.2s;
`;

const PatternPlacementIcon = styled.img`
  width: 58px;
  height: 36px;
  object-fit: contain;
  opacity: ${props => props.selected ? 1 : 0.72};
  filter: ${props => props.selected ? 'none' : 'grayscale(55%)'};
  transition: transform 0.16s ease, opacity 0.16s ease, filter 0.16s ease;

  ${OptionCard}:hover & {
    transform: translateY(-1px);
    opacity: 1;
  }
`;

const shapeOptions = [
  'ROUND', 'SQUARE', 'RECTANGLE', 'OVAL', 'CAT_EYE', 'AVIATOR', 'BUTTERFLY', 'CLUBMASTER'
];
const materialOptions = [
  'METAL', 'TITANIUM', 'POLYMER', 'WOOD', 'TR90', 'CARBON_FIBER', 'ULTEM', 'CORN', 'ACETATE', 'SILICON', 'GOLD_PLATED', 'SILVER_PLATED', 'MIXED'
];
const sexOptions = ['MALE', 'FEMALE', 'UNISEX', 'ENFANT'];
const designOptions = ['PERCEE', 'CERCLE', 'NYLOR', 'MIXED'];
const patternPlacementOptions = [
  { value: 'FRAME_FRONT', label: 'Frame front', icon: '/patterns/FrameFront.svg' },
  { value: 'ARMS', label: 'Arms', icon: '/patterns/Arms.svg' },
  { value: 'WHOLE_FRAME', label: 'Whole frame', icon: '/patterns/WholeFrame.svg' }
];

const frameColorPalette = [
  { label: 'Black', value: '#111827' },
  { label: 'Charcoal', value: '#374151' },
  { label: 'Brown', value: '#7c4a2d' },
  { label: 'Tortoise', value: '#8b5a2b', fill: 'linear-gradient(135deg, #3b2414 0%, #8b5a2b 38%, #d6a35d 62%, #1f130b 100%)' },
  { label: 'Navy', value: '#1e3a8a' },
  { label: 'Burgundy', value: '#7f1d1d' },
  { label: 'Olive', value: '#4d7c0f' },
  { label: 'Clear', value: '#f8fafc', fill: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 48%, #ffffff 100%)' },
  { label: 'Golden', value: '#d4af37', fill: 'linear-gradient(135deg, #8a6f16 0%, #d4af37 38%, #fff1a8 55%, #b88917 100%)' },
  { label: 'Metallic Gold', value: '#c99700', fill: 'linear-gradient(135deg, #6b4e00 0%, #c99700 35%, #ffe875 52%, #9a6b00 100%)' },
  { label: 'Silver', value: '#c0c0c0', fill: 'linear-gradient(135deg, #7c8794 0%, #c0c0c0 38%, #f8fafc 55%, #9ca3af 100%)' },
  { label: 'Metallic Silver', value: '#a8b0b8', fill: 'linear-gradient(135deg, #64748b 0%, #a8b0b8 35%, #f1f5f9 52%, #7f8a96 100%)' },
  { label: 'Rose Gold', value: '#b76e79', fill: 'linear-gradient(135deg, #7f3f46 0%, #b76e79 38%, #ffd7cf 55%, #96505a 100%)' },
  { label: 'Champagne', value: '#d6c7a1', fill: 'linear-gradient(135deg, #9f8d62 0%, #d6c7a1 45%, #fff5cf 60%, #b7a275 100%)' }
];

const ColorPickerField = ({ label, value, onChange }) => {
  const { t } = useTranslation();
  const selectedOption = frameColorPalette.find((option) => option.value.toLowerCase() === String(value || '').toLowerCase());
  const previewFill = selectedOption?.fill || value || '#ffffff';
  const selectedLabel = selectedOption?.label || (value ? 'Custom color' : 'Choose color');

  return (
    <Field>
      <Label>{label}</Label>
      <ColorPickerPanel>
        <ColorPickerHeader>
          <ColorPreview empty={!value} fill={previewFill} aria-hidden="true" />
          <span>{selectedLabel}</span>
        </ColorPickerHeader>
        <ColorSwatchGrid>
          {frameColorPalette.map((option) => (
            <ColorSwatchButton
              key={option.label}
              type="button"
              selected={String(value || '').toLowerCase() === option.value.toLowerCase()}
              onClick={() => onChange(option.value)}
              aria-pressed={String(value || '').toLowerCase() === option.value.toLowerCase()}
              title={option.label}
            >
              <PaletteColorDot fill={option.fill || option.value} />
              <span>{option.label}</span>
            </ColorSwatchButton>
          ))}
        </ColorSwatchGrid>
        <CustomColorLabel>
          {t('frameStockPage.pickCustomColor')}
          <HiddenColorInput type="color" value={value || '#6366f1'} onChange={(event) => onChange(event.target.value)} />
        </CustomColorLabel>
      </ColorPickerPanel>
    </Field>
  );
};

const FrameStockPage = ({ refreshKey = 0, pulledFrame = null } = {}) => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState({ stores: [] });
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationType, setLocationType] = useState('STORE');
  const [selectedLocationId, setSelectedLocationId] = useState('');

  const [filters, setFilters] = useState({
    shape: '',
    material: '',
    sex: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [products, setProducts] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [adjustModal, setAdjustModal] = useState({
    open: false,
    item: null,
    qty: 0
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    item: null
  });
  // Read-only "view details" modal (shared by inventory + admin dashboard).
  const [detailItem, setDetailItem] = useState(null);
  const [returnModal, setReturnModal] = useState({
    open: false,
    item: null,
    qty: 1,
    submitting: false
  });
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [collapsedBrands, setCollapsedBrands] = useState({});
  const [createNotice, setCreateNotice] = useState({
    open: false,
    type: 'success',
    message: ''
  });

  const [productForm, setProductForm] = useState({
    sku: '',
    quantity: '',
    brand: '',
    reference: '',
    locationType: 'STORE',
    locationId: '',
    priceDzd: '',
    shape: '',
    material: '',
    mixedMaterial1: '',
    mixedMaterial2: '',
    design: '',
    sex: '',
    pattern: false,
    patternPlacement: '',
    attachment: false,
    attachmentCount: '',
    color1: '',
    color2: '',
    color3: '',
    color4: '',
    lensWidthMm: '',
    bridgeMm: '',
    armMm: '',
    barcode: '',
    imageUrl: '',
    sellByMonths: ''
  });
  const [colorCount, setColorCount] = useState(1);
  const [sameColorForAll, setSameColorForAll] = useState(true);
  const [samePatternForAll, setSamePatternForAll] = useState(true);
  const [sameAttachmentForAll, setSameAttachmentForAll] = useState(true);
  // Variant groups when quantity > 1 and an attribute varies between frames.
  // Each unit is a GROUP of identical frames with its own qty, e.g. "10 black".
  // Shape: { qty, color1..4, colorCount, pattern, patternPlacement, attachment, attachmentCount }.
  const [units, setUnits] = useState([]);
  const allSameForAll = sameColorForAll && samePatternForAll && sameAttachmentForAll;
  const newUnit = () => ({ qty: '', color1: '', color2: '', color3: '', color4: '', colorCount: 1, pattern: false, patternPlacement: '', attachment: false, attachmentCount: '' });
  // How many frames have been assigned across the variant groups vs the target.
  const assignedQty = units.reduce((sum, u) => sum + (parseInt(u.qty) || 0), 0);
  const targetQty = parseInt(productForm.quantity) || 0;
  const centralStore = useMemo(() => (
    locations.stores.find((store) => store.isCentralWarehouse)
  ), [locations.stores]);

  const resetProductForm = () => {
    // Stock locations are STORE only. Central is a Store; labs/admin
    // locations no longer hold stock.
    const defaultLocationType = 'STORE';
    const defaultLocationId = canManageOwnStore
      ? userStoreId
      : (centralStore?._id || centralStore?.id || userStoreId || '');

    setProductForm({
      sku: '',
      quantity: '',
      brand: '',
      reference: '',
      locationType: defaultLocationType,
      locationId: defaultLocationId,
      priceDzd: '',
      shape: '',
      material: '',
      mixedMaterial1: '',
      mixedMaterial2: '',
      design: '',
      sex: '',
      pattern: false,
      patternPlacement: '',
      attachment: false,
      attachmentCount: '',
      color1: '',
      color2: '',
      color3: '',
      color4: '',
      lensWidthMm: '',
      bridgeMm: '',
      armMm: '',
      barcode: '',
      imageUrl: '',
      sellByMonths: ''
    });
    setColorCount(1);
    setSameColorForAll(true);
    setSamePatternForAll(true);
    setSameAttachmentForAll(true);
    setUnits([]);
    setEditingProduct(null);
  };

  // Seed the variant groups the first time the user breaks "same for all".
  // Start with two EMPTY groups — the user types one quantity and the leftover
  // auto-fills the other (see setUnitQty). Existing groups are kept.
  const seedUnits = () => {
    setUnits((prev) => (prev.length > 0 ? prev : [newUnit(), newUnit()]));
  };

  // Add an empty group for the user to fill.
  const addUnit = () => setUnits((prev) => [...prev, newUnit()]);

  const removeUnit = (fi) =>
    setUnits((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== fi) : prev));

  // Set one group's quantity. If that leaves exactly one still-empty group and
  // there are frames left over, drop the remainder into that group automatically.
  const setUnitQty = (fi, value) => {
    setUnits((prev) => {
      const next = prev.map((u, i) => (i === fi ? { ...u, qty: value } : u));
      const total = parseInt(productForm.quantity) || 0;
      const assigned = next.reduce((sum, u) => sum + (parseInt(u.qty) || 0), 0);
      const remaining = total - assigned;
      const emptyIdx = next
        .map((u, i) => ({ u, i }))
        .filter(({ u, i }) => i !== fi && (u.qty === '' || u.qty === null || u.qty === undefined))
        .map(({ i }) => i);
      if (emptyIdx.length === 1 && remaining > 0) {
        next[emptyIdx[0]] = { ...next[emptyIdx[0]], qty: String(remaining) };
      }
      return next;
    });
  };

  // ── Shared (product-level) colour slot add/remove ──────────────────────────
  const removeColorSlot = (idx) => {
    setProductForm((prev) => {
      const cols = [prev.color1, prev.color2, prev.color3, prev.color4];
      cols.splice(idx - 1, 1);
      cols.push('');
      return { ...prev, color1: cols[0], color2: cols[1], color3: cols[2], color4: cols[3] };
    });
    setColorCount((c) => Math.max(1, c - 1));
  };

  // ── Per-frame field / colour helpers ───────────────────────────────────────
  const setUnitField = (fi, field, value) =>
    setUnits((prev) => prev.map((u, i) => (i === fi ? { ...u, [field]: value } : u)));

  const addUnitColor = (fi) =>
    setUnits((prev) => prev.map((u, i) => (i === fi ? { ...u, colorCount: Math.min((u.colorCount || 1) + 1, 4) } : u)));

  const removeUnitColor = (fi, idx) =>
    setUnits((prev) => prev.map((u, i) => {
      if (i !== fi) return u;
      const cols = [u.color1, u.color2, u.color3, u.color4];
      cols.splice(idx - 1, 1);
      cols.push('');
      return { ...u, color1: cols[0], color2: cols[1], color3: cols[2], color4: cols[3], colorCount: Math.max(1, (u.colorCount || 1) - 1) };
    }));

  const openEditModal = (item) => {
    const product = item.frameProduct;
    setEditingProduct(product);
    setProductForm({
      sku: product.sku || '',
      quantity: item.qty ?? '',
      brand: product.brand || '',
      reference: product.reference || '',
      locationType: 'STORE',
      locationId: '',
      priceDzd: product.priceDzd ?? '',
      shape: product.shape || '',
      material: product.material || '',
      mixedMaterial1: product.mixedMaterial1 || '',
      mixedMaterial2: product.mixedMaterial2 || '',
      design: product.design || '',
      sex: product.sex || '',
      pattern: !!product.pattern,
      patternPlacement: product.patternPlacement || '',
      attachment: !!product.attachment,
      attachmentCount: product.attachmentCount != null && product.attachmentCount !== '' ? String(product.attachmentCount) : '',
      color1: product.color1 || '',
      color2: product.color2 || '',
      color3: product.color3 || '',
      color4: product.color4 || '',
      lensWidthMm: product.lensWidthMm ?? '',
      bridgeMm: product.bridgeMm ?? '',
      armMm: product.armMm ?? '',
      barcode: product.barcode || '',
      imageUrl: product.imageUrl || '',
      sellByMonths: product.sellByMonths ?? ''
    });
    const cc = [product.color2, product.color3, product.color4].filter(Boolean).length + 1;
    setColorCount(Math.min(4, Math.max(1, cc)));
    setSameColorForAll(true);
    setSamePatternForAll(true);
    setSameAttachmentForAll(true);
    setUnits([]);
    setShowProductForm(true);
  };

  const showCreateNotice = (type, message) => {
    setCreateNotice({ open: true, type, message });
  };

  const closeCreateNotice = () => {
    setCreateNotice({ open: false, type: 'success', message: '' });
  };

  // Print State
  const [printItem, setPrintItem] = useState(null);
  const printLabelRef = useRef(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user')) || {};
    } catch (error) {
      return {};
    }
  }, []);

  // Advisors see what an admin sees; they can change it only when the admin
  // put "Frame stock" on Edit for them (the server enforces it too).
  const advisor = advisorAccessFor(user);
  const isAdmin = user.role === 'admin' || !!advisor;
  const advisorMayEdit = !advisor || advisor['frame-stock'] === 'e';
  const isCentralViewer = ['inventory_manager', 'operations_manager'].includes(user.role);
  const isStoreManager = user.role === 'store_manager';
  const userStoreId = user.store?.id || user.store?._id || (typeof user.store === 'string' ? user.store : '') || user.storeId || '';
  const selectedStore = locations.stores.find((store) => String(store._id || store.id) === String(selectedLocationId));
  const isViewingCentralStore = !!selectedStore?.isCentralWarehouse || (isCentralViewer && String(selectedLocationId) === String(userStoreId));
  // Task switches. "Frames" (MG Central) manages central's stock and the whole
  // catalog. "Store stock" lets store staff add the frames their store already
  // has and correct them — in their own store only (the server enforces it).
  const canManageCentral = advisorMayEdit && (isAdmin || (isCentralViewer && hasTask(user, 'central.frames'))) && isViewingCentralStore;
  const canManageOwnStore = !canManageCentral && !isViewingCentralStore && hasTask(user, 'store.stock')
    && !!userStoreId && String(selectedLocationId) === String(userStoreId);
  const canManage = canManageCentral || canManageOwnStore;
  // In store mode only frames this store added can have their details fixed.
  const canEditDetails = (product) => canManageCentral
    || (canManageOwnStore && String(product?.createdBy?.storeId || '') === String(userStoreId));
  const canReturnToCentral = (isStoreManager || hasTask(user, 'store.stock')) && !isViewingCentralStore && !isAdmin;
  const isReadOnly = !canManage;
  const storeName = user.store?.name || 'Optical Store';

  // Stats
  const stats = useMemo(() => {
    const totalFrames = items.length;
    const totalItems = items.reduce((sum, item) => sum + (item.qty || 0), 0);
    const lowStock = items.filter(i => (i.qty || 0) < 3).length;
    return { totalFrames, totalItems, lowStock };
  }, [items]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await getFrameProducts();
        setProducts(data.products || []);
      } catch (error) {
        console.error('Error loading frame products:', error);
      }
    };
    loadProducts();
  }, []);

  useEffect(() => {
    if (!isAdmin && !isCentralViewer) {
      // Anyone bound to a Store (retail manager, central warehouse
      // inventory/operations manager, courier) reads/writes that
      // store's stock. Other non-admin roles fall through to a no-op.
      const isStoreBound = ['store_manager', 'inventory_manager', 'operations_manager', 'courier'].includes(user.role)
        || hasTask(user, 'store.stock');
      if (isStoreBound) {
        setLocationType('STORE');
        setSelectedLocationId(userStoreId);
        setProductForm((prev) => ({ ...prev, locationType: 'STORE', locationId: userStoreId }));
      }
      return;
    }
    const loadLocations = async () => {
      setLocationsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/locations', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setLocations({
          stores: response.data.stores || []
        });
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadLocations();
  }, [isAdmin, isCentralViewer, user, userStoreId]);

  useEffect(() => {
    if ((!isAdmin && !isCentralViewer) || locationsLoading || selectedLocationId) {
      return;
    }
    const list = locations.stores;
    // Prefer the central warehouse so admin and central staff share a single
    // inventory view by default. Inventory/operations managers are bound to
    // the central store, so fall back to their own store when the
    // isCentralWarehouse flag isn't set, and only then to the first entry.
    const central = locationType === 'STORE'
      ? (list.find((s) => s.isCentralWarehouse)
        || list.find((s) => String(s._id || s.id) === String(userStoreId)))
      : null;
    const defaultLocation = central || list[0];
    const defaultId = defaultLocation?._id || defaultLocation?.id || '';
    if (defaultId) {
      setSelectedLocationId(defaultId);
    }
  }, [isAdmin, isCentralViewer, locationsLoading, locationType, locations.stores, selectedLocationId, userStoreId]);

  const loadStock = async () => {
    if (!selectedLocationId) return;
    setLoading(true);
    try {
      const data = await getFrameStock({
        location: { type: locationType, id: selectedLocationId },
        filters: {
          shape: filters.shape || undefined,
          material: filters.material || undefined,
          sex: filters.sex || undefined
        }
      });
      setItems(data.items || []);
      setEdits({});
      setNotice(null);
    } catch (error) {
      console.error('Error loading frame stock:', error);
      const serverMessage = error.response?.data?.message;
      const serverDetail = error.response?.data?.error;
      const message = serverDetail ? `${serverMessage || 'Server error'}: ${serverDetail}` : (serverMessage || 'Failed to load frame stock');
      setNotice({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStock();
  }, [selectedLocationId, locationType, filters, refreshKey]);

  useEffect(() => {
    if (!pulledFrame?.product?._id) return;
    const productId = pulledFrame.product._id;
    const nextQty = Number(pulledFrame.storeRemaining ?? pulledFrame.qty ?? 0) || 0;
    setItems((prev) => {
      let found = false;
      const next = prev.map((item) => {
        if (item.frameProduct?._id !== productId) return item;
        found = true;
        return { ...item, qty: nextQty, initialQty: item.initialQty ?? nextQty };
      });
      if (found) return next;
      return [...next, {
        id: `virtual-${productId}`,
        _id: `virtual-${productId}`,
        qty: nextQty,
        initialQty: nextQty,
        reservedQty: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        frameProduct: pulledFrame.product,
      }];
    });
  }, [pulledFrame]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const openAdjustModal = (item, mode = 'add') => {
    if (!canManage) return;
    const productId = item.frameProduct?._id;
    const currentQty = edits[productId] !== undefined ? edits[productId] : item.qty;
    setAdjustModal({
      open: true,
      item,
      qty: currentQty
    });
  };

  const closeAdjustModal = () => {
    setAdjustModal({
      open: false,
      item: null,
      qty: 0
    });
  };

  const applyAdjustment = async () => {
    if (!adjustModal.item) return;
    const productId = adjustModal.item.frameProduct?._id;
    const nextQty = Number.parseInt(adjustModal.qty, 10);
    if (Number.isNaN(nextQty) || nextQty < 0) {
      setNotice({ type: 'error', message: 'Enter a valid quantity' });
      return;
    }
    setAdjustModal((prev) => ({ ...prev, saving: true }));
    try {
      await updateFrameStock({
        location: { type: locationType, id: selectedLocationId },
        updates: [{ frameProductId: productId, qty: nextQty }]
      });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      setNotice({ type: 'success', message: 'Stock updated' });
      await loadStock();
      closeAdjustModal();
    } catch (error) {
      const serverMessage = error.response?.data?.message;
      const serverDetail = error.response?.data?.error;
      setNotice({ type: 'error', message: serverDetail ? `${serverMessage || 'Server error'}: ${serverDetail}` : (serverMessage || 'Failed to update stock') });
      setAdjustModal((prev) => ({ ...prev, saving: false }));
    }
  };

  // Photo straight from the list (camera on a tablet) for a frame without one.
  const [rowPhotoBusy, setRowPhotoBusy] = useState(null);
  const [rowCameraFor, setRowCameraFor] = useState(null);
  const handleRowPhoto = async (product, file) => {
    setRowPhotoBusy(product._id);
    try {
      const shrunk = await downscaleImage(file, { maxDim: 1000, quality: 0.82 });
      await setFrameProductImage(product._id, shrunk);
      showCreateNotice('success', t('framePhoto.saved'));
      await loadStock();
    } catch (err) {
      showCreateNotice('error', err.response?.data?.message || t('framePhoto.uploadFailed'));
    } finally {
      setRowPhotoBusy(null);
    }
  };

  const openDeleteModal = (item) => {
    if (!canManageCentral) return;
    setDeleteModal({
      open: true,
      item
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      open: false,
      item: null
    });
  };

  const confirmDeleteProduct = async () => {
    if (!canManageCentral) return;
    if (!deleteModal.item) return;
    const product = deleteModal.item.frameProduct;
    try {
      await deleteFrameProduct(product._id);
      setProducts((prev) => prev.filter((p) => p._id !== product._id));
      setItems((prev) => prev.filter((item) => item.frameProduct?._id !== product._id));
      setEdits((prev) => {
        const updated = { ...prev };
        delete updated[product._id];
        return updated;
      });
      setNotice({ type: 'success', message: 'Frame deleted successfully' });
      await loadStock();
    } catch (error) {
      console.error('Delete frame product error:', error);
      const serverMessage = error.response?.data?.message;
      const serverDetail = error.response?.data?.error;
      const message = serverDetail ? `${serverMessage || 'Server error'}: ${serverDetail}` : (serverMessage || 'Failed to delete frame');
      setNotice({ type: 'error', message });
    } finally {
      closeDeleteModal();
    }
  };

  const confirmBulkDelete = async () => {
    if (!canManageCentral) return;
    if (selectedIds.size === 0) {
      setNotice({ type: 'error', message: 'No frames selected' });
      return;
    }
    try {
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await deleteFrameProduct(id);
      }
      setProducts((prev) => prev.filter((p) => !selectedIds.has(p._id)));
      setItems((prev) => prev.filter((item) => !selectedIds.has(item.frameProduct?._id)));
      setEdits((prev) => {
        const updated = { ...prev };
        ids.forEach((id) => delete updated[id]);
        return updated;
      });
      setNotice({ type: 'success', message: 'Selected frames deleted successfully' });
      clearSelection();
      await loadStock();
    } catch (error) {
      console.error('Bulk delete frame products error:', error);
      const serverMessage = error.response?.data?.message;
      const serverDetail = error.response?.data?.error;
      const message = serverDetail ? `${serverMessage || 'Server error'}: ${serverDetail}` : (serverMessage || 'Failed to delete selected frames');
      setNotice({ type: 'error', message });
    } finally {
      setBulkDeleteModal(false);
    }
  };

  const handleSave = async () => {
    if (!canManage) return;
    const updates = Object.entries(edits).map(([key, qty]) => ({
      frameProductId: key,
      qty
    }));
    if (updates.length === 0) {
      setNotice({ type: 'error', message: 'No changes to save' });
      return;
    }
    setSaving(true);
    try {
      await updateFrameStock({
        location: { type: locationType, id: selectedLocationId },
        updates
      });
      setNotice({ type: 'success', message: 'Frame stock updated' });
      await loadStock();
    } catch (error) {
      console.error('Error saving frame stock:', error);
      const serverMessage = error.response?.data?.message;
      const serverDetail = error.response?.data?.error;
      const message = serverDetail ? `${serverMessage || 'Server error'}: ${serverDetail}` : (serverMessage || 'Failed to save frame stock');
      setNotice({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!canManage) return;
    try {
      const requiredFields = [
        ['brand', 'Brand'],
        ['quantity', 'Quantity'],
        ['priceDzd', 'Price'],
        ['sellByMonths', 'Sell-by (Months)'],
        ['shape', 'Shape'],
        ['material', 'Material'],
        ['design', 'Design'],
        ['sex', 'Sex'],
        ['lensWidthMm', 'Lens Width'],
        ['bridgeMm', 'Bridge'],
        ['armMm', 'Arm'],
        ...(editingProduct ? [] : [['locationId', 'Location']])
      ];
      const missingField = requiredFields.find(([key]) => productForm[key] === '' || productForm[key] === null || productForm[key] === undefined);
      if (missingField) {
        showCreateNotice('error', `${missingField[1]} is required`);
        return;
      }
      if (productForm.material === 'MIXED' && (!productForm.mixedMaterial1 || !productForm.mixedMaterial2)) {
        showCreateNotice('error', 'Select both mixed materials');
        return;
      }
      const isMultiFrame = !editingProduct && Number(productForm.quantity) > 1 && !allSameForAll;
      const attachmentIsShared = editingProduct || sameAttachmentForAll || Number(productForm.quantity) <= 1;
      const colorIsShared = editingProduct || sameColorForAll || Number(productForm.quantity) <= 1;
      const patternIsShared = editingProduct || samePatternForAll || Number(productForm.quantity) <= 1;

      if (attachmentIsShared && productForm.attachment && productForm.attachmentCount === '') {
        showCreateNotice('error', 'Enter how many attachments');
        return;
      }
      if (patternIsShared && productForm.pattern && !productForm.patternPlacement) {
        showCreateNotice('error', 'Select where the pattern is');
        return;
      }
      if (colorIsShared && !productForm.color1) {
        showCreateNotice('error', 'Color 1 is required');
        return;
      }
      if (isMultiFrame) {
        if (units.length === 0 || units.some((u) => !(parseInt(u.qty) > 0))) {
          showCreateNotice('error', 'Enter a quantity for every variant group');
          return;
        }
        if (assignedQty !== targetQty) {
          showCreateNotice('error', `Variant quantities must add up to ${targetQty} (currently ${assignedQty})`);
          return;
        }
        if (!sameColorForAll && units.some((u) => !u.color1)) {
          showCreateNotice('error', 'Select a color for every variant group');
          return;
        }
        if (!sameAttachmentForAll && units.some((u) => u.attachment && u.attachmentCount === '')) {
          showCreateNotice('error', 'Enter the attachment count for every variant group');
          return;
        }
        if (!samePatternForAll && units.some((u) => u.pattern && !u.patternPlacement)) {
          showCreateNotice('error', 'Select where the pattern is for every patterned group');
          return;
        }
      }

      const name = `${productForm.brand} ${productForm.reference}`.trim();

      if (editingProduct) {
        const payload = {
          ...productForm,
          name,
          sku: productForm.sku || editingProduct.sku,
          color2: colorCount >= 2 ? productForm.color2 : '',
          color3: colorCount >= 3 ? productForm.color3 : '',
          color4: colorCount >= 4 ? productForm.color4 : '',
          patternPlacement: productForm.pattern ? productForm.patternPlacement : '',
          sellByMonths: productForm.sellByMonths ? Number(productForm.sellByMonths) : null,
          priceDzd: productForm.priceDzd ? Number(productForm.priceDzd) : null
        };
        const result = await updateFrameProduct(editingProduct._id, payload);
        setProducts((prev) => prev.map((product) => product._id === result.product._id ? result.product : product));
        if (selectedLocationId) {
          await updateFrameStock({
            location: { type: locationType, id: selectedLocationId },
            updates: [{ frameProductId: result.product._id, qty: productForm.quantity || 0 }]
          });
        }
        setShowProductForm(false);
        resetProductForm();
        showCreateNotice('success', 'Product updated');
        await loadStock();
        return;
      }

      const basePayload = {
        ...productForm,
        name,
        sellByMonths: productForm.sellByMonths ? Number(productForm.sellByMonths) : null,
        priceDzd: productForm.priceDzd ? Number(productForm.priceDzd) : null
      };
      const targetLocationId = canManageOwnStore ? userStoreId : productForm.locationId;
      const targetLocationType = canManageOwnStore ? 'STORE' : productForm.locationType;

      if (allSameForAll || Number(productForm.quantity) <= 1) {
        const generatedSku = productForm.brand.substring(0, 3).toUpperCase() + '-' + Date.now().toString().slice(-6);
        const payload = {
          ...basePayload,
          sku: generatedSku,
          color2: colorCount >= 2 ? productForm.color2 : '',
          color3: colorCount >= 3 ? productForm.color3 : '',
          color4: colorCount >= 4 ? productForm.color4 : '',
          patternPlacement: productForm.pattern ? productForm.patternPlacement : '',
          attachmentCount: productForm.attachment ? productForm.attachmentCount : ''
        };
        const result = await createFrameProduct(payload);
        setProducts((prev) => {
          const next = prev.some((product) => product._id === result.product._id)
            ? prev.map((product) => product._id === result.product._id ? result.product : product)
            : [...prev, result.product];
          return next.sort((a, b) => a.name.localeCompare(b.name));
        });
        if (targetLocationId) {
          if (!selectedLocationId) setSelectedLocationId(targetLocationId);
          await updateFrameStock({
            location: { type: targetLocationType, id: targetLocationId },
            updates: [{ frameProductId: result.product._id, qty: productForm.quantity || 0 }]
          });
          showCreateNotice('success', 'Product created and added to stock');
          await loadStock();
        } else {
          showCreateNotice('success', 'Product created. Add a location to view stock.');
        }
        setPrintItem({ frameProduct: result.product, sku: generatedSku });
      } else {
        const createdResults = [];
        for (let i = 0; i < units.length; i++) {
          const u = units[i];
          const groupQty = parseInt(u.qty) || 0;
          const generatedSku = productForm.brand.substring(0, 3).toUpperCase() + '-' + Date.now().toString().slice(-6) + '-' + (i + 1);
          // Resolve each attribute from the shared value or the per-group one.
          const colors = sameColorForAll
            ? { color1: productForm.color1, color2: colorCount >= 2 ? productForm.color2 : '', color3: colorCount >= 3 ? productForm.color3 : '', color4: colorCount >= 4 ? productForm.color4 : '' }
            : { color1: u.color1, color2: u.colorCount >= 2 ? u.color2 : '', color3: u.colorCount >= 3 ? u.color3 : '', color4: u.colorCount >= 4 ? u.color4 : '' };
          const pattern = samePatternForAll ? productForm.pattern : u.pattern;
          const patternPlacement = pattern ? (samePatternForAll ? productForm.patternPlacement : u.patternPlacement) : '';
          const attachment = sameAttachmentForAll ? productForm.attachment : u.attachment;
          const attachmentCount = (sameAttachmentForAll ? productForm.attachmentCount : u.attachmentCount);
          const payload = {
            ...basePayload,
            quantity: groupQty,
            sku: generatedSku,
            ...colors,
            imageUrl: sameColorForAll ? (productForm.imageUrl || '') : (u.imageUrl || ''),
            pattern,
            patternPlacement,
            attachment,
            attachmentCount: attachment ? attachmentCount : ''
          };
          const result = await createFrameProduct(payload);
          createdResults.push({ result, generatedSku, groupQty });
        }
        setProducts((prev) => {
          let next = prev;
          createdResults.forEach(({ result }) => {
            next = next.some((product) => product._id === result.product._id)
              ? next.map((product) => product._id === result.product._id ? result.product : product)
              : [...next, result.product];
          });
          return next.sort((a, b) => a.name.localeCompare(b.name));
        });
        if (targetLocationId) {
          if (!selectedLocationId) setSelectedLocationId(targetLocationId);
          await updateFrameStock({
            location: { type: targetLocationType, id: targetLocationId },
            updates: createdResults.map(({ result, groupQty }) => ({ frameProductId: result.product._id, qty: groupQty }))
          });
          showCreateNotice('success', `${createdResults.length} variant${createdResults.length > 1 ? 's' : ''} (${assignedQty} frames) created and added to stock`);
          await loadStock();
        } else {
          showCreateNotice('success', `${createdResults.length} variants created. Add a location to view stock.`);
        }
        const last = createdResults[createdResults.length - 1];
        if (last) setPrintItem({ frameProduct: last.result.product, sku: last.generatedSku });
      }

      setShowProductForm(false);
      resetProductForm();

    } catch (error) {
      console.error('Create frame product error:', error);
      const serverMessage = error.response?.data?.message;
      const serverDetail = error.response?.data?.error;
      const message = serverDetail ? `${serverMessage || 'Server error'}: ${serverDetail}` : (serverMessage || 'Failed to create product');
      showCreateNotice('error', message);
    }
  };

  // Print a single label in a self-contained popup that contains ONLY the
  // <div class="print-label"> — no React app shell, no wrappers, no global
  // styles. html/body/.print-label are all locked to exactly 40mm × 20mm with
  // overflow:hidden and absolute-positioned children (no flex/grid/transform),
  // so the content can never exceed one label or spill onto the next sticker.
  const handlePrint = () => {
    if (!printItem) return;

    // Reuse the QR already rendered in the preview modal (inline SVG) so the
    // printed code is identical across Chrome and Firefox.
    const qrSvg = printLabelRef.current?.querySelector('svg')?.outerHTML || '';

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const name = esc(printItem.frameProduct?.name || printItem.name || storeName || '');
    const code = esc(printItem.frameProduct?.sku || printItem.sku || '');
    const priceVal = printItem.frameProduct?.priceDzd ?? printItem.priceDzd;
    const priceHtml = (priceVal != null && priceVal !== '')
      ? `<div class="label-price">${esc(priceVal)} DZD</div>` : '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Label ${code}</title><style>
@page { size: 40mm 20mm; margin: 0; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* font-size/line-height 0 on the page root kills stray whitespace line-boxes
   between elements that can spill a few px onto a phantom 2nd (blank) label.
   Real text restores its own size via the .label-* classes. */
html, body { width: 40mm; height: 19mm; max-height: 19mm; margin: 0; padding: 0; overflow: hidden; background: #fff; font-size: 0; line-height: 0; }
/* Outer container fills the label and centers content both axes. Inner row
   holds QR (left) + text (right). No rotation, offsets, or absolute pos. */
.print-label { width: 40mm; height: 20mm; display: flex; align-items: center; justify-content: center; box-sizing: border-box; overflow: hidden; padding: 1.5mm; page-break-after: avoid; break-after: avoid; page-break-inside: avoid; break-inside: avoid; }
.label-inner { display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 1.5mm; width: 100%; }
.qr-section { width: 15mm; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.qr-section svg, .qr-section .qr svg, .qr { width: 14mm !important; height: 14mm !important; display: block; image-rendering: pixelated; }
.text-section { flex: 1; min-width: 0; overflow: hidden; }
.label-title { font-size: 12px; font-weight: 700; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.label-code { font-size: 11px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.label-price { font-size: 11px; font-weight: 600; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style></head><body><div class="print-label"><div class="label-inner"><div class="qr-section"><div class="qr">${qrSvg}</div></div><div class="text-section"><div class="label-title">${name}</div><div class="label-code">${code}</div>${priceHtml}</div></div></div><script>window.onload=function(){window.focus();window.print();};window.onafterprint=function(){window.close();};<\/script></body></html>`;

    const win = window.open('', 'qr-label-print', 'width=900,height=700,left=200,top=80');
    if (!win) { window.alert('Please allow popups to print the label.'); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Safety net if onafterprint doesn't fire (e.g. print-to-PDF).
    setTimeout(() => { try { if (!win.closed) win.close(); } catch (e) { /* ignore */ } }, 3000);
  };

  const getFrameDisplayName = (product = {}) => {
    const name = String(product.name || '').trim();
    const brand = String(product.brand || '').trim();

    return name || brand || 'Unnamed Frame';
  };

  // Client-side filtering for Search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const lower = searchTerm.toLowerCase();
    return items.filter((item) => {
      const product = item.frameProduct || {};
      return (
        String(product.name || '').toLowerCase().includes(lower) ||
        String(product.sku || '').toLowerCase().includes(lower) ||
        String(product.brand || '').toLowerCase().includes(lower) ||
        getFrameDisplayName(product).toLowerCase().includes(lower)
      );
    });
  }, [items, searchTerm]);

  const frameNameGroups = useMemo(() => {
    const groups = new Map();
    filteredItems.forEach((item) => {
      const product = item.frameProduct || {};
      const frameName = getFrameDisplayName(product);
      if (!groups.has(frameName)) {
        groups.set(frameName, { frameName, items: [], totalQty: 0, lowCount: 0 });
      }
      const group = groups.get(frameName);
      const qty = edits[product._id] !== undefined ? edits[product._id] : item.qty;
      group.items.push(item);
      group.totalQty += Number(qty) || 0;
      if ((Number(qty) || 0) < 3) group.lowCount += 1;
    });
    return Array.from(groups.values()).sort((a, b) => a.frameName.localeCompare(b.frameName));
  }, [filteredItems, edits]);

  const isBrandCollapsed = (brand) => {
    if (searchTerm) return false;
    return collapsedBrands[brand] ?? true;
  };

  const toggleBrandCollapsed = (brand) => {
    setCollapsedBrands((prev) => ({
      ...prev,
      [brand]: !(prev[brand] ?? true)
    }));
  };

  const openReturnModal = (item) => {
    if (!item?.frameProduct?._id || item.qty <= 0) return;
    setReturnModal({
      open: true,
      item,
      qty: 1,
      submitting: false
    });
  };

  const closeReturnModal = () => {
    if (returnModal.submitting) return;
    setReturnModal({
      open: false,
      item: null,
      qty: 1,
      submitting: false
    });
  };

  const handleReturnFrameToCentral = async () => {
    const item = returnModal.item;
    const product = item?.frameProduct;
    if (!product?._id) return;
    const availableQty = Number(item.qty) || 0;
    const qty = Math.max(1, parseInt(returnModal.qty, 10) || 1);
    if (qty > availableQty) {
      setNotice({ type: 'error', message: `You can return up to ${availableQty} unit(s).` });
      return;
    }
    setReturnModal((prev) => ({ ...prev, submitting: true }));
    try {
      await returnFrameToCentral({ frameProductId: product._id, qty });
      setNotice({ type: 'success', message: `Returned ${qty} ${product.name} to central.` });
      closeReturnModal();
      await loadStock();
    } catch (error) {
      setNotice({ type: 'error', message: error.response?.data?.message || 'Failed to return frame to central' });
      setReturnModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const renderFrameRow = (item) => {
    const product = item.frameProduct || {};
    const qty = edits[product._id] !== undefined ? edits[product._id] : item.qty;
    const status = qty === 0 ? 'out' : qty < 3 ? 'low' : 'instock';
    const statusLabel = qty === 0 ? 'Out of Stock' : qty < 3 ? 'Low Stock' : 'In Stock';
    const Icon = qty === 0 ? AlertCircle : qty < 3 ? AlertTriangle : CheckCircle;
    const baselineQty = item.initialQty ?? qty;
    const percent = baselineQty > 0 ? Math.round((qty / baselineQty) * 100) : 0;
    const patternPlacementLabel = patternPlacementOptions.find((option) => option.value === product.patternPlacement)?.label;

    return (
      <tr key={item.id || product._id}>
        {canManageCentral && (
          <td>
            <input
              type="checkbox"
              checked={selectedIds.has(product._id)}
              onChange={() => toggleSelect(product._id)}
              aria-label={`Select ${product.name}`}
            />
          </td>
        )}
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          {product.imageUrl ? (
            <button type="button" onClick={() => setDetailItem(item)} title={t('frameStockPage.viewDetails')}
              style={{ width: 56, height: 40, flex: 'none', padding: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer' }}>
              <img src={product.imageUrl} alt={product.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </button>
          ) : canEditDetails(product) ? (
            <label title={t('framePhoto.addPhoto')}
              onClick={(e) => { if (cameraAvailable() && rowPhotoBusy !== product._id) { e.preventDefault(); setRowCameraFor(product); } }}
              style={{ width: 56, height: 40, flex: 'none', borderRadius: 8, border: '1.5px dashed #cbd5e1', background: '#f8fafc', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              {rowPhotoBusy === product._id ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Camera size={17} />}
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                disabled={rowPhotoBusy === product._id}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleRowPhoto(product, f); }} />
            </label>
          ) : null}
          <FrameNameStack>
            <FrameBrandBadge>{product.brand ? `Model ${product.brand}` : t('frameStockPage.frame')}</FrameBrandBadge>
            <div style={{ fontWeight: '700', color: '#1e293b' }}>{product.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{product.sku}</div>
          </FrameNameStack>
          </div>
        </td>
        <td>
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            <Chip>{product.shape}</Chip>
            <Chip>{product.material === 'MIXED' ? t('frameStockPage.mixed') : product.material}</Chip>
            {product.design && <Chip>{product.design}</Chip>}
            <Chip>{product.sex}</Chip>
            {product.pattern && <Chip>{t('frameStockPage.pattern')}{patternPlacementLabel ? `: ${patternPlacementLabel}` : ''}</Chip>}
          </div>
        </td>
        <td>
          <div style={{ display: 'flex', marginLeft: '6px' }}>
            {[product.color1, product.color2, product.color3, product.color4].filter(Boolean).map((color, i) => (
              <ColorDot key={`${color}-${i}`} color={color} />
            ))}
          </div>
        </td>
        <td style={{ color: '#64748b', fontSize: '0.875rem' }}>
          {product.lensWidthMm}-{product.bridgeMm}-{product.armMm}
        </td>
        <td style={{ color: '#64748b', fontSize: '0.875rem' }}>
          {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : t('frameStockPage.nA')}
        </td>
        <td>
          <StatusBadge status={status}>
            <Icon size={12} />
            {statusLabel}
          </StatusBadge>
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <QtyDisplay>
              <span>{qty}</span>
              <QtyPercent>{percent}{t('frameStockPage.remaining')}</QtyPercent>
            </QtyDisplay>
            {!isReadOnly && edits[product._id] !== undefined && (
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1' }} title={t('frameStockPage.unsavedChange')} />
            )}
          </div>
        </td>
        <td>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <IconButton onClick={() => setDetailItem(item)} title={t('frameStockPage.viewDetails')}>
              <Eye size={16} />
            </IconButton>
            {canManage && (
              <IconButton onClick={() => openAdjustModal(item)} title={t('frameStockPage.editQuantity')}>
                <Edit3 size={16} />
              </IconButton>
            )}
            {canEditDetails(item.frameProduct) && (
              <IconButton onClick={() => openEditModal(item)} title={t('frameStockPage.editFrameDetails')}>
                <Box size={16} />
              </IconButton>
            )}
            {canManageCentral && (
              <IconButton onClick={() => openDeleteModal(item)} title={t('frameStockPage.deleteFrame')}>
                <Trash2 size={16} />
              </IconButton>
            )}
            <IconButton onClick={() => setPrintItem(item)} title={t('frameStockPage.printLabel')}>
              <Printer size={16} />
            </IconButton>
            {canReturnToCentral && item.qty > 0 && (
              <IconButton onClick={() => openReturnModal(item)} title={t('frameStockPage.returnToCentral')}>
                <TrendingUp size={16} />
              </IconButton>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <Container>
        <Header>
          <TitleColumn>
            <PageEyebrow>{t('frameStock.eyebrow')}</PageEyebrow>
            <PageTitle>{t('frameStock.title')}</PageTitle>
            <Subtitle>{t('frameStock.subtitle')}</Subtitle>
          </TitleColumn>
          <HeaderActions>
            {canManageCentral && selectedIds.size > 0 && (
              <ActionButton variant="danger" onClick={() => setBulkDeleteModal(true)}>
                <Trash2 size={18} />
                {t('common.delete')} ({selectedIds.size})
              </ActionButton>
            )}
            {canManage && (
              <ActionButton onClick={() => { resetProductForm(); setShowProductForm(true); }}>
                <Plus size={18} />
                {t('frameStock.addProduct')}
              </ActionButton>
            )}
          </HeaderActions>
        </Header>

        {/* Stats Dashboard */}
        <StatsGrid>
          <StatCard accent="linear-gradient(90deg, #6366f1 0%, #818cf8 100%)">
            <StatIcon bg="#e0e7ff" color="#4f46e5">
              <LayoutGrid size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel>{t('frameStock.totalFrames')}</StatLabel>
              <StatValue>{stats.totalFrames}</StatValue>
            </StatContent>
          </StatCard>
          <StatCard accent="linear-gradient(90deg, #10b981 0%, #34d399 100%)">
            <StatIcon bg="#dcfce7" color="#166534">
              <Box size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel>{t('frameStock.totalItems')}</StatLabel>
              <StatValue>{stats.totalItems}</StatValue>
            </StatContent>
          </StatCard>
          <StatCard accent="linear-gradient(90deg, #f43f5e 0%, #fb7185 100%)">
            <StatIcon bg="#fee2e2" color="#991b1b">
              <AlertTriangle size={24} />
            </StatIcon>
            <StatContent>
              <StatLabel>{t('frameStock.lowStock')}</StatLabel>
              <StatValue>{stats.lowStock}</StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>

        {/* Modern Filter Bar */}
        <FilterBar>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              placeholder={t('frameStock.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          {(isAdmin || isCentralViewer) && (
            <SelectGroup>
              <StyledSelect value={locationType} onChange={(e) => {
                setLocationType(e.target.value);
                setSelectedLocationId('');
              }}>
                <option value="STORE">{t('frameStock.store')}</option>
              </StyledSelect>
              <StyledSelect
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                disabled={locationsLoading}
              >
                <option value="">{locationsLoading ? t('frameStockPage.loading') : t('frameStockPage.selectLocation')}</option>
                {locations.stores.map((location) => (
                  <option key={location._id || location.id} value={location._id || location.id}>
                    {location.name}
                  </option>
                ))}
              </StyledSelect>
            </SelectGroup>
          )}

          <SelectGroup>
            <StyledSelect value={filters.shape} onChange={(e) => setFilters({ ...filters, shape: e.target.value })}>
              <option value="">{t('frameStock.allShapes')}</option>
              {shapeOptions.map(shape => <option key={shape} value={shape}>{shape}</option>)}
            </StyledSelect>
            <StyledSelect value={filters.material} onChange={(e) => setFilters({ ...filters, material: e.target.value })}>
              <option value="">{t('frameStock.allMaterials')}</option>
              {materialOptions.map(mat => <option key={mat} value={mat}>{mat}</option>)}
            </StyledSelect>
            <StyledSelect value={filters.sex} onChange={(e) => setFilters({ ...filters, sex: e.target.value })}>
              <option value="">{t('frameStock.allSex')}</option>
              {sexOptions.map(sex => <option key={sex} value={sex}>{sex}</option>)}
            </StyledSelect>
          </SelectGroup>
        </FilterBar>

        {/* Main Content */}
        <TableCard>
          <TableToolbar>
            <TableTitle>
              <strong>{t('frameStockPage.frameCatalog')}</strong>
              <span>{filteredItems.length} result{filteredItems.length === 1 ? '' : 's'} {t('frameStockPage.groupedByFrameName')}</span>
            </TableTitle>
            <BrandMeta>
              <BrandCountPill>{frameNameGroups.length} group{frameNameGroups.length === 1 ? '' : 's'}</BrandCountPill>
              <span>{stats.totalItems} {t('frameStockPage.totalItems')}</span>
            </BrandMeta>
          </TableToolbar>
          {notice && (
            <div style={{ padding: '1rem', borderBottom: '1px solid #f1f5f9', background: notice.type === 'error' ? '#fef2f2' : '#f0fdf4', color: notice.type === 'error' ? '#991b1b' : '#166534', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <AlertCircle size={18} />
              {notice.message}
            </div>
          )}

          {loading ? (
            <LoadingState>
              <LoadingSpinner />
              <strong>{t('frameStockPage.loadingFrameInventory')}</strong>
              <span>{t('frameStockPage.fetchingTheLatestStockLevels')}</span>
            </LoadingState>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <StockTable>
                  <thead>
                    <tr>
                      {canManageCentral && (
                        <th style={{ width: '48px' }}>
                          <input
                            type="checkbox"
                            checked={filteredItems.length > 0 && filteredItems.every((item) => selectedIds.has(item.frameProduct?._id))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedIds(new Set(filteredItems.map((item) => item.frameProduct?._id).filter(Boolean)));
                              } else {
                                clearSelection();
                              }
                            }}
                            aria-label={t('frameStockPage.selectAllFrames')}
                          />
                        </th>
                      )}
                      <th>{t('frameStockPage.frameInfo')}</th>
                      <th>{t('frameStockPage.attributes')}</th>
                      <th>{t('frameStockPage.colors')}</th>
                      <th>{t('frameStockPage.dimensions')}</th>
                      <th>{t('frameStockPage.addedOn')}</th>
                      <th>{t('frameStockPage.status')}</th>
                      <th>{t('frameStockPage.inStock')}</th>
                      <th>{t('frameStockPage.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {frameNameGroups.map((group) => {
                      const collapsed = isBrandCollapsed(group.frameName);
                      return (
                        <React.Fragment key={group.frameName}>
                          <tr>
                            <BrandGroupCell colSpan={canManageCentral ? 9 : 8}>
                              <BrandToggle type="button" onClick={() => toggleBrandCollapsed(group.frameName)} aria-expanded={!collapsed}>
                                <BrandTitle>
                                  {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                                  <strong>{group.frameName}</strong>
                                </BrandTitle>
                                <BrandMeta>
                                  <BrandCountPill>{group.items.length} frame{group.items.length === 1 ? '' : 's'}</BrandCountPill>
                                  <span>{group.totalQty} {t('frameStockPage.inStock2')}</span>
                                  {group.lowCount > 0 && <span>{group.lowCount} {t('frameStockPage.lowOut')}</span>}
                                </BrandMeta>
                              </BrandToggle>
                            </BrandGroupCell>
                          </tr>
                          {!collapsed && group.items.map(renderFrameRow)}
                        </React.Fragment>
                      );
                    })}
                    {filteredItems.length === 0 && (
                      <tr>
                        <td colSpan={canManageCentral ? 9 : 8}>
                          <EmptyState>
                            <LayoutGrid size={30} color="#94a3b8" />
                            <strong>{t('frameStockPage.noFramesFound')}</strong>
                            <span>{t('frameStockPage.tryChangingTheSearchFilters')}</span>
                          </EmptyState>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </StockTable>
              </div>
              {!isReadOnly && Object.keys(edits).length > 0 && (
                <DirtyBar>
                  <DirtyHint>{Object.keys(edits).length} {t('frameStockPage.unsavedStockChange')}{Object.keys(edits).length === 1 ? '' : 's'}</DirtyHint>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <Button variant="secondary" onClick={() => setEdits({})}>{t('frameStockPage.cancel')}</Button>
                    <Button onClick={handleSave} disabled={saving}>{saving ? t('frameStockPage.saving') : t('frameStockPage.saveChanges')}</Button>
                  </div>
                </DirtyBar>
              )}
            </>
          )}
        </TableCard>

        {/* Return to Central Modal */}
        {returnModal.open && returnModal.item && (
          <NoticeOverlay onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeReturnModal();
          }}>
            <ReturnModalCard role="dialog" aria-modal="true" aria-labelledby="return-frame-title">
              <ReturnModalHeader>
                <div>
                  <h3 id="return-frame-title">{t('frameStockPage.returnFrameToCentral')}</h3>
                  <p>{t('frameStockPage.moveStockOutOfThis')}</p>
                </div>
                <CloseModalButton type="button" onClick={closeReturnModal} aria-label={t('frameStockPage.closeReturnDialog')}>
                  <X size={18} />
                </CloseModalButton>
              </ReturnModalHeader>
              <ReturnModalBody>
                <ReturnSummary>
                  <SummaryBox>
                    <span>{t('frameStockPage.frame')}</span>
                    <strong>{returnModal.item.frameProduct?.name || 'Frame'}</strong>
                  </SummaryBox>
                  <SummaryBox>
                    <span>{t('frameStockPage.storeStock')}</span>
                    <strong>{returnModal.item.qty || 0} available</strong>
                  </SummaryBox>
                </ReturnSummary>
                <QuantityField>
                  <label htmlFor="return-frame-qty">{t('frameStockPage.quantityToReturn')}</label>
                  <input
                    id="return-frame-qty"
                    type="number"
                    min="1"
                    max={returnModal.item.qty || 1}
                    value={returnModal.qty}
                    autoFocus
                    onChange={(event) => setReturnModal((prev) => ({ ...prev, qty: event.target.value }))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handleReturnFrameToCentral();
                      if (event.key === 'Escape') closeReturnModal();
                    }}
                  />
                  <small>{t('frameStockPage.thisImmediatelyDecreasesStoreStock')}</small>
                </QuantityField>
                <ReturnActions>
                  <Button type="button" variant="secondary" onClick={closeReturnModal} disabled={returnModal.submitting}>
                    {t('frameStockPage.cancel')}
                  </Button>
                  <Button type="button" onClick={handleReturnFrameToCentral} disabled={returnModal.submitting}>
                    {returnModal.submitting ? t('frameStockPage.returning') : t('frameStockPage.returnToCentral')}
                  </Button>
                </ReturnActions>
              </ReturnModalBody>
            </ReturnModalCard>
          </NoticeOverlay>
        )}

        {/* Adjust Quantity Modal */}
        {adjustModal.open && adjustModal.item && ReactDOM.createPortal(
          <NoticeOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) closeAdjustModal(); }}>
            <NoticeModal style={{ maxWidth: '520px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>{t('frameStockPage.editQuantity')}</h3>
                <button onClick={closeAdjustModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
              </div>
              <div style={{ color: '#475569', marginBottom: '0.75rem' }}>
                {adjustModal.item.frameProduct?.name} ({adjustModal.item.frameProduct?.sku})
              </div>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <Field>
                  <Label>{t('frameStockPage.newQuantity')}</Label>
                  <Input
                    type="number"
                    min="0"
                    autoFocus
                    value={adjustModal.qty}
                    onChange={(e) => setAdjustModal((prev) => ({ ...prev, qty: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyAdjustment(); if (e.key === 'Escape') closeAdjustModal(); }}
                  />
                </Field>
                <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                  {t('frameStockPage.currentStock')} {edits[adjustModal.item.frameProduct?._id] !== undefined ? edits[adjustModal.item.frameProduct?._id] : adjustModal.item.qty}
                </div>
              </div>
              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" onClick={closeAdjustModal} disabled={adjustModal.saving}>{t('frameStockPage.cancel')}</Button>
                <Button type="button" onClick={applyAdjustment} disabled={adjustModal.saving}>{adjustModal.saving ? t('frameStockPage.saving') : t('frameStockPage.save')}</Button>
              </div>
            </NoticeModal>
          </NoticeOverlay>,
          document.body
        )}

        {/* Delete Frame Modal */}
        {deleteModal.open && deleteModal.item && ReactDOM.createPortal(
          <NoticeOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) closeDeleteModal(); }}>
            <NoticeModal style={{ maxWidth: '520px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>{t('frameStockPage.deleteFrame')}</h3>
                <button onClick={closeDeleteModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
              </div>
              <div style={{ color: '#475569', marginBottom: '1rem' }}>
                {t('frameStockPage.thisWillRemoveTheFrame')}
              </div>
              <div style={{ fontWeight: 600, marginBottom: '1.5rem' }}>
                {deleteModal.item.frameProduct?.name} ({deleteModal.item.frameProduct?.sku})
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" onClick={closeDeleteModal}>{t('frameStockPage.cancel')}</Button>
                <Button type="button" onClick={confirmDeleteProduct}>{t('frameStockPage.deleteFrame')}</Button>
              </div>
            </NoticeModal>
          </NoticeOverlay>,
          document.body
        )}

        {/* View Frame Details Modal (read-only) */}
        {detailItem && detailItem.frameProduct && ReactDOM.createPortal((() => {
          const p = detailItem.frameProduct;
          const placementLabel = patternPlacementOptions.find((o) => o.value === p.patternPlacement)?.label;
          const materialLabel = p.material === 'MIXED'
            ? `Mixed${[p.mixedMaterial1, p.mixedMaterial2].filter(Boolean).length ? ` (${[p.mixedMaterial1, p.mixedMaterial2].filter(Boolean).join(' + ')})` : ''}`
            : (p.material || '—');
          const colors = [p.color1, p.color2, p.color3, p.color4].filter(Boolean);
          const cb = p.createdBy;
          const Row = ({ label, children }) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>{label}</span>
              <span style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 600, textAlign: 'right' }}>{children}</span>
            </div>
          );
          return (
            <NoticeOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) setDetailItem(null); }}>
              <NoticeModal style={{ maxWidth: '560px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{p.name || 'Frame'}</h3>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.2rem' }}>{p.sku}{p.brand ? ` · Model ${p.brand}` : ''}</div>
                  </div>
                  <button onClick={() => setDetailItem(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
                </div>

                {p.imageUrl && (
                  <img src={p.imageUrl} alt={p.name} style={{ width: '100%', maxHeight: '180px', objectFit: 'contain', borderRadius: '12px', background: '#f8fafc', marginBottom: '1rem' }} />
                )}

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Row label={t('frameStockPage.shapeLabel')}>{p.shape || '—'}</Row>
                  <Row label={t('frameStockPage.materialLabel')}>{materialLabel}</Row>
                  {p.design && <Row label={t('frameStockPage.designLabel')}>{p.design}</Row>}
                  <Row label={t('frameStockPage.sexLabel')}>{p.sex || '—'}</Row>
                  <Row label={t('frameStockPage.colors')}>
                    {colors.length ? (
                      <span style={{ display: 'inline-flex', gap: '0.25rem', verticalAlign: 'middle' }}>
                        {colors.map((c, i) => <ColorDot key={`${c}-${i}`} color={c} />)}
                      </span>
                    ) : '—'}
                  </Row>
                  <Row label={t('frameStockPage.pattern')}>{p.pattern ? (placementLabel ? `Yes · ${placementLabel}` : t('frameStockPage.yes')) : t('frameStockPage.no')}</Row>
                  <Row label={t('frameStockPage.attachment')}>{p.attachment ? `Yes${p.attachmentCount ? ` · ${p.attachmentCount}` : ''}` : t('frameStockPage.no')}</Row>
                  <Row label={t('frameStockPage.dimensionsMm')}>{[p.lensWidthMm, p.bridgeMm, p.armMm].filter((v) => v !== '' && v != null).join('-') || '—'}</Row>
                  <Row label={t('frameStockPage.priceDzdLabel')}>{p.priceDzd != null && p.priceDzd !== '' ? Number(p.priceDzd).toLocaleString() : '—'}</Row>
                  {p.sellByMonths != null && p.sellByMonths !== '' && <Row label={t('frameStockPage.sellByMonthsLabel')}>{p.sellByMonths}</Row>}
                  <Row label={t('frameStockPage.inStockHere')}>{detailItem.qty ?? '—'}</Row>
                  <Row label={t('frameStockPage.dateAdded')}>{detailItem.createdAt ? new Date(detailItem.createdAt).toLocaleString() : t('frameStockPage.nA')}</Row>
                  <Row label={t('frameStockPage.addedBy')}>
                    {cb && cb.name
                      ? `${cb.name}${cb.employeeId ? ` (${cb.employeeId})` : ''}${cb.role ? ` · ${cb.role.replace(/_/g, ' ')}` : ''}`
                      : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{t('frameStockPage.notRecorded')}</span>}
                  </Row>
                </div>

                <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  {canEditDetails(detailItem?.frameProduct) && (
                    <Button type="button" variant="secondary" onClick={() => { const it = detailItem; setDetailItem(null); openEditModal(it); }}>
                      <Box size={16} /> {t('frameStockPage.edit')}
                    </Button>
                  )}
                  <Button type="button" onClick={() => setDetailItem(null)}>{t('frameStockPage.close')}</Button>
                </div>
              </NoticeModal>
            </NoticeOverlay>
          );
        })(), document.body)}

        {/* Bulk Delete Modal */}
        {bulkDeleteModal && ReactDOM.createPortal(
          <NoticeOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) setBulkDeleteModal(false); }}>
            <NoticeModal style={{ maxWidth: '520px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>{t('frameStockPage.deleteSelectedFrames')}</h3>
                <button onClick={() => setBulkDeleteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
              </div>
              <div style={{ color: '#475569', marginBottom: '1rem' }}>
                {t('frameStockPage.thisWillRemove')} {selectedIds.size} frame{selectedIds.size === 1 ? '' : 's'} {t('frameStockPage.andTheirStockFromAll')}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" onClick={() => setBulkDeleteModal(false)}>{t('frameStockPage.cancel')}</Button>
                <Button type="button" onClick={confirmBulkDelete}>{t('frameStockPage.deleteSelected')}</Button>
              </div>
            </NoticeModal>
          </NoticeOverlay>,
          document.body
        )}

        {/* Add Product Modal — rendered via portal to <body> so the fixed
            overlay anchors to the viewport, not a transformed ancestor. */}
        {showProductForm && ReactDOM.createPortal(
          <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowProductForm(false); resetProductForm(); } }}>
            <ModalCard>
              <ModalHeader>
                <div>
                  <ModalKicker>{t('frameStockPage.centralStoreInventory')}</ModalKicker>
                  <ModalTitle>{editingProduct ? t('frameStockPage.editFrameProduct') : t('frameStockPage.addFrameProduct')}</ModalTitle>
                  <ModalSubtitle>
                    {t('frameStockPage.addTheProductDetailsOnce')}
                  </ModalSubtitle>
                </div>
                <CloseButton type="button" onClick={() => { setShowProductForm(false); resetProductForm(); }} aria-label={t('frameStockPage.closeFrameProductForm')}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>

              <ModalBody>
              <FormSectionCard>
              <SectionHeader>
                <div>
                  <FormSectionTitle first>{t('frameStockPage.productDetails')}</FormSectionTitle>
                  <SectionDescription>{t('frameStockPage.startWithTheInformationStaff')}</SectionDescription>
                </div>
                <RequiredPill>{t('frameStockPage.required')}</RequiredPill>
              </SectionHeader>
              <ControlsRow>
                <Field>
                  <Label>{t('frameStockPage.brand')}</Label>
                  <Input placeholder={t('frameStockPage.eGRayBan')} value={productForm.brand} onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })} />
                </Field>
                <Field>
                  <Label>{t('frameStockPage.reference')}</Label>
                  <Input placeholder={t('frameStockPage.modelOrSupplierReference')} value={productForm.reference} onChange={(e) => setProductForm({ ...productForm, reference: e.target.value })} />
                </Field>
                <Field>
                  <Label>{t('frameStockPage.quantity')}</Label>
                  <Input
                    type="number"
                    min={editingProduct ? '0' : '1'}
                    placeholder={t('frameStockPage.eG12')}
                    value={productForm.quantity}
                    onChange={(e) => {
                      const qty = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                      setProductForm({ ...productForm, quantity: qty });
                    }}
                  />
                </Field>
                <Field>
                  <Label>{t('frameStockPage.sellByMonths')}</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder={t('frameStockPage.eG3')}
                    value={productForm.sellByMonths}
                    onChange={(e) => setProductForm({ ...productForm, sellByMonths: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>{t('frameStockPage.priceDzd')}</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder={t('frameStockPage.eG25000')}
                    value={productForm.priceDzd}
                    onChange={(e) => setProductForm({ ...productForm, priceDzd: e.target.value })}
                  />
                </Field>
              </ControlsRow>

              {!editingProduct && (
                <FriendlyNotice>
                  <MapPin size={14} />
                  <span>{t('frameStockPage.stockWillBeAddedAt')} <strong>{centralStore?.name || user.store?.name || 'your central warehouse'}</strong>{t('frameStockPage.branchStoresReceiveFramesBy')}</span>
                </FriendlyNotice>
              )}
              </FormSectionCard>

              <FormSectionCard>
              <SectionHeader>
                <div>
                  <FormSectionTitle>{t('frameStockPage.colorsPatternAttachment')}</FormSectionTitle>
                  <SectionDescription>{t('frameStockPage.pickTheVisibleFinishFirst')}</SectionDescription>
                </div>
              </SectionHeader>

              {/* Upfront "same for all?" toggles when more than one frame. */}
              {!editingProduct && Number(productForm.quantity) > 1 && (
                <SameForAllPanel>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, padding: '0.5rem 0 0.25rem' }}>
                    {t('frameStockPage.areAll')} {productForm.quantity} {t('frameStockPage.framesIdenticalTurnOffAnything')}
                  </div>
                  {[
                    { label: 'Same color', val: sameColorForAll, set: setSameColorForAll },
                    { label: 'Same pattern', val: samePatternForAll, set: setSamePatternForAll },
                    { label: 'Same attachment', val: sameAttachmentForAll, set: setSameAttachmentForAll },
                  ].map((q) => (
                    <ToggleRow key={q.label} on={q.val}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span className="lbl">{q.label} {t('frameStockPage.forAllFrames')}</span>
                        <span className="hint">{q.val ? t('frameStockPage.sameForEveryFrame') : t('frameStockPage.setPerVariantGroupBelow')}</span>
                      </div>
                      <Switch type="button" on={q.val} onClick={() => { const turningOff = q.val; q.set(!q.val); if (turningOff) seedUnits(); }} aria-label={q.label} />
                    </ToggleRow>
                  ))}
                </SameForAllPanel>
              )}

              {/* Shared colors (single product, or "same color for all"). */}
              {(editingProduct || sameColorForAll || Number(productForm.quantity) <= 1) && (
                <ControlsRow style={{ marginBottom: '1rem' }}>
                  <ColorPickerField
                    label={t('frameStockPage.color1')}
                    value={productForm.color1}
                    onChange={(value) => setProductForm({ ...productForm, color1: value })}
                  />
                  {colorCount >= 2 && (
                    <ColorPickerField
                      label={<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('frameStockPage.color2')}<RemoveColorBtn type="button" onClick={() => removeColorSlot(2)}><X size={14} /> {t('frameStockPage.remove')}</RemoveColorBtn></span>}
                      value={productForm.color2}
                      onChange={(value) => setProductForm({ ...productForm, color2: value })}
                    />
                  )}
                  {colorCount >= 3 && (
                    <ColorPickerField
                      label={<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('frameStockPage.color3')}<RemoveColorBtn type="button" onClick={() => removeColorSlot(3)}><X size={14} /> {t('frameStockPage.remove')}</RemoveColorBtn></span>}
                      value={productForm.color3}
                      onChange={(value) => setProductForm({ ...productForm, color3: value })}
                    />
                  )}
                  {colorCount >= 4 && (
                    <ColorPickerField
                      label={<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('frameStockPage.color4')}<RemoveColorBtn type="button" onClick={() => removeColorSlot(4)}><X size={14} /> {t('frameStockPage.remove')}</RemoveColorBtn></span>}
                      value={productForm.color4}
                      onChange={(value) => setProductForm({ ...productForm, color4: value })}
                    />
                  )}
                  {colorCount < 4 && (
                    <Field style={{ alignSelf: 'end' }}>
                      <Button type="button" variant="secondary" onClick={() => setColorCount((prev) => Math.min(prev + 1, 4))}><Plus size={16} /> {t('frameStockPage.addColor')}</Button>
                    </Field>
                  )}
                </ControlsRow>
              )}

              {/* Shared pattern. */}
              {(editingProduct || samePatternForAll || Number(productForm.quantity) <= 1) && (
                <Field style={{ marginBottom: '0.75rem' }}>
                  <CheckboxLine>
                    <input
                      type="checkbox"
                      checked={productForm.pattern}
                      onChange={(e) => setProductForm({
                        ...productForm,
                        pattern: e.target.checked,
                        patternPlacement: e.target.checked ? productForm.patternPlacement : ''
                      })}
                    />
                    <span>{t('frameStockPage.thisFrameHasAPattern')}</span>
                  </CheckboxLine>
                  {productForm.pattern && (
                    <div style={{ marginTop: '0.7rem' }}>
                      <Label>{t('frameStockPage.whereIsThePattern')}</Label>
                      <SelectionGrid>
                        {patternPlacementOptions.map((option) => (
                          <OptionCard
                            key={option.value}
                            selected={productForm.patternPlacement === option.value}
                            onClick={() => setProductForm({ ...productForm, patternPlacement: option.value })}
                            onDoubleClick={() => setProductForm({ ...productForm, patternPlacement: '' })}
                          >
                            <PatternPlacementIcon src={option.icon} alt="" selected={productForm.patternPlacement === option.value} />
                            {option.label}
                          </OptionCard>
                        ))}
                      </SelectionGrid>
                    </div>
                  )}
                </Field>
              )}

              {/* Shared attachment. */}
              {(editingProduct || sameAttachmentForAll || Number(productForm.quantity) <= 1) && (
                <Field style={{ marginBottom: '0.75rem' }}>
                  <CheckboxLine>
                    <input type="checkbox" checked={productForm.attachment} onChange={(e) => setProductForm({ ...productForm, attachment: e.target.checked, attachmentCount: e.target.checked ? productForm.attachmentCount : '' })} />
                    <span>{t('frameStockPage.thisFrameHasAnAttachment')}</span>
                  </CheckboxLine>
                  {productForm.attachment && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.5rem' }}>
                      <Label style={{ margin: 0 }}>{t('frameStockPage.howMany')}</Label>
                      <Input
                        type="number" min="0" max="9" style={{ width: '70px' }}
                        value={productForm.attachmentCount}
                        onChange={(e) => setProductForm({ ...productForm, attachmentCount: e.target.value.replace(/[^0-9]/g, '').slice(0, 1) })}
                        onKeyDown={(e) => { if (['-', '+', 'e', '.'].includes(e.key)) e.preventDefault(); }}
                      />
                    </div>
                  )}
                </Field>
              )}

              {/* Photo — one per colour. Shared here when every frame is the same colour. */}
              {(editingProduct || sameColorForAll || Number(productForm.quantity) <= 1) && (
                <div style={{ marginBottom: '1rem' }}>
                  <FramePhotoPicker
                    label={t('framePhoto.photo')}
                    value={productForm.imageUrl || ''}
                    onChange={(url) => setProductForm((prev) => ({ ...prev, imageUrl: url }))}
                  />
                </div>
              )}

              {/* Variant groups: each is a batch of identical frames with its own qty. */}
              {!editingProduct && Number(productForm.quantity) > 1 && !allSameForAll && (
                <PerFrameList>
                  <div style={{ fontSize: '0.8rem', color: assignedQty === targetQty ? '#15803d' : '#b45309', fontWeight: 700, padding: '0.25rem 0' }}>
                    {assignedQty} of {targetQty} {t('frameStockPage.framesAssigned')}{assignedQty === targetQty ? ' ✓' : ` — ${targetQty - assignedQty > 0 ? `${targetQty - assignedQty} left to assign` : `${assignedQty - targetQty} over`}`}
                  </div>
                  {units.map((u, fi) => (
                    <PerFrameCard key={fi}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>{t('frameStockPage.variantGroup')} {fi + 1}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Label style={{ margin: 0 }}>{t('frameStockPage.quantity')}</Label>
                          <Input
                            type="number" min="1" style={{ width: '80px' }}
                            placeholder={t('frameStockPage.eG10')}
                            value={u.qty}
                            onChange={(e) => setUnitQty(fi, e.target.value.replace(/[^0-9]/g, ''))}
                            onKeyDown={(e) => { if (['-', '+', 'e', '.'].includes(e.key)) e.preventDefault(); }}
                          />
                          {units.length > 1 && (
                            <RemoveColorBtn type="button" onClick={() => removeUnit(fi)}><X size={14} /> {t('frameStockPage.remove')}</RemoveColorBtn>
                          )}
                        </div>
                      </div>

                      {!sameColorForAll && (
                        <Field style={{ marginBottom: '0.6rem' }}>
                          <Label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {t('frameStockPage.colors2')}
                            {(u.colorCount || 1) < 4 && (
                              <RemoveColorBtn type="button" style={{ color: '#6366f1' }} onClick={() => addUnitColor(fi)}><Plus size={13} /> {t('frameStockPage.addColor2')}</RemoveColorBtn>
                            )}
                          </Label>
                          <ControlsRow style={{ marginTop: '0.35rem' }}>
                            <ColorPickerField
                              label={t('frameStockPage.color1')}
                              value={u.color1}
                              onChange={(value) => setUnitField(fi, 'color1', value)}
                            />
                            {(u.colorCount || 1) >= 2 && (
                              <ColorPickerField
                                label={<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('frameStockPage.color2')}<RemoveColorBtn type="button" onClick={() => removeUnitColor(fi, 2)}><X size={13} /></RemoveColorBtn></span>}
                                value={u.color2}
                                onChange={(value) => setUnitField(fi, 'color2', value)}
                              />
                            )}
                            {(u.colorCount || 1) >= 3 && (
                              <ColorPickerField
                                label={<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('frameStockPage.color3')}<RemoveColorBtn type="button" onClick={() => removeUnitColor(fi, 3)}><X size={13} /></RemoveColorBtn></span>}
                                value={u.color3}
                                onChange={(value) => setUnitField(fi, 'color3', value)}
                              />
                            )}
                            {(u.colorCount || 1) >= 4 && (
                              <ColorPickerField
                                label={<span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{t('frameStockPage.color4')}<RemoveColorBtn type="button" onClick={() => removeUnitColor(fi, 4)}><X size={13} /></RemoveColorBtn></span>}
                                value={u.color4}
                                onChange={(value) => setUnitField(fi, 'color4', value)}
                              />
                            )}
                          </ControlsRow>
                        </Field>
                      )}

                      {!samePatternForAll && (
                        <Field style={{ marginBottom: '0.6rem' }}>
                          <CheckboxLine>
                            <input
                              type="checkbox"
                              checked={u.pattern}
                              onChange={(e) => {
                                setUnitField(fi, 'pattern', e.target.checked);
                                if (!e.target.checked) setUnitField(fi, 'patternPlacement', '');
                              }}
                            />
                            <span>{t('frameStockPage.hasAPattern')}</span>
                          </CheckboxLine>
                          {u.pattern && (
                            <div style={{ marginTop: '0.7rem' }}>
                              <Label>{t('frameStockPage.whereIsThePattern')}</Label>
                              <SelectionGrid>
                                {patternPlacementOptions.map((option) => (
                                  <OptionCard
                                    key={option.value}
                                    selected={u.patternPlacement === option.value}
                                    onClick={() => setUnitField(fi, 'patternPlacement', option.value)}
                                    onDoubleClick={() => setUnitField(fi, 'patternPlacement', '')}
                                  >
                                    <PatternPlacementIcon src={option.icon} alt="" selected={u.patternPlacement === option.value} />
                                    {option.label}
                                  </OptionCard>
                                ))}
                              </SelectionGrid>
                            </div>
                          )}
                        </Field>
                      )}

                      {!sameAttachmentForAll && (
                        <>
                          <CheckboxLine>
                            <input type="checkbox" checked={u.attachment} onChange={(e) => { setUnitField(fi, 'attachment', e.target.checked); if (!e.target.checked) setUnitField(fi, 'attachmentCount', ''); }} />
                            <span>{t('frameStockPage.hasAnAttachment')}</span>
                          </CheckboxLine>
                          {u.attachment && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.5rem' }}>
                              <Label style={{ margin: 0 }}>{t('frameStockPage.howMany')}</Label>
                              <Input
                                type="number" min="0" max="9" style={{ width: '70px' }}
                                value={u.attachmentCount}
                                onChange={(e) => setUnitField(fi, 'attachmentCount', e.target.value.replace(/[^0-9]/g, '').slice(0, 1))}
                                onKeyDown={(e) => { if (['-', '+', 'e', '.'].includes(e.key)) e.preventDefault(); }}
                              />
                            </div>
                          )}
                        </>
                      )}
                      {!sameColorForAll && (
                        <div style={{ marginTop: '0.6rem' }}>
                          <FramePhotoPicker
                            label={t('framePhoto.photoForThisColour')}
                            value={u.imageUrl || ''}
                            onChange={(url) => setUnitField(fi, 'imageUrl', url)}
                          />
                        </div>
                      )}
                    </PerFrameCard>
                  ))}
                  <Button type="button" variant="secondary" onClick={addUnit}><Plus size={16} /> {t('frameStockPage.addVariantGroup')}</Button>
                </PerFrameList>
              )}
              </FormSectionCard>

              <FormSectionCard>
              <SectionHeader>
                <div>
                  <FormSectionTitle>{t('frameStockPage.dimensionsMm')}</FormSectionTitle>
                  <SectionDescription>{t('frameStockPage.useTheNumbersPrintedInside')}</SectionDescription>
                </div>
              </SectionHeader>
              <ControlsRow>
                <Field>
                  <Label>{t('frameStockPage.lensWidthMm')}</Label>
                  <Input type="number" min="0" placeholder={t('frameStockPage.eG52')} value={productForm.lensWidthMm} onChange={(e) => setProductForm({ ...productForm, lensWidthMm: e.target.value })} />
                </Field>
                <Field>
                  <Label>{t('frameStockPage.bridgeMm')}</Label>
                  <Input type="number" min="0" placeholder={t('frameStockPage.eG18')} value={productForm.bridgeMm} onChange={(e) => setProductForm({ ...productForm, bridgeMm: e.target.value })} />
                </Field>
                <Field>
                  <Label>{t('frameStockPage.armMm')}</Label>
                  <Input type="number" min="0" placeholder={t('frameStockPage.eG140')} value={productForm.armMm} onChange={(e) => setProductForm({ ...productForm, armMm: e.target.value })} />
                </Field>
              </ControlsRow>
              </FormSectionCard>

              <FormSectionCard>
              <SectionHeader>
                <div>
                  <FormSectionTitle>{t('frameStockPage.frameAttributes')}</FormSectionTitle>
                  <SectionDescription>{t('frameStockPage.selectOneOptionInEach')}</SectionDescription>
                </div>
              </SectionHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Field>
                  <Label>{t('frameStockPage.shape')}</Label>
                  <SelectionGrid>
                    {shapeOptions.map((shape) => (
                      <OptionCard
                        key={shape}
                        selected={productForm.shape === shape}
                        onClick={() => setProductForm({ ...productForm, shape })}
                        onDoubleClick={() => setProductForm({ ...productForm, shape: '' })}
                      >
                        <IconImage
                          src={`/shapes/${shape.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}.svg`}
                          alt={shape}
                          selected={productForm.shape === shape}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        {shape.replace('_', ' ')}
                      </OptionCard>
                    ))}
                  </SelectionGrid>
                </Field>

                <Field>
                  <Label>{t('frameStockPage.material')}</Label>
                  <SelectionGrid>
                    {materialOptions.map((material) => (
                      <OptionCard
                        key={material}
                        selected={productForm.material === material}
                        onClick={() => setProductForm({
                          ...productForm,
                          material,
                          mixedMaterial1: material === 'MIXED' ? productForm.mixedMaterial1 : '',
                          mixedMaterial2: material === 'MIXED' ? productForm.mixedMaterial2 : ''
                        })}
                        onDoubleClick={() => setProductForm({ ...productForm, material: '', mixedMaterial1: '', mixedMaterial2: '' })}
                      >
                        {material.replace('_', ' ')}
                      </OptionCard>
                    ))}
                  </SelectionGrid>
                </Field>

                {productForm.material === 'MIXED' && (
                  <>
                    <Field>
                      <Label>{t('frameStockPage.mixedMaterial1')}</Label>
                      <SelectionGrid>
                        {materialOptions.filter(m => m !== 'MIXED').map((material) => (
                          <OptionCard
                            key={material}
                            selected={productForm.mixedMaterial1 === material}
                            onClick={() => setProductForm({ ...productForm, mixedMaterial1: material })}
                            onDoubleClick={() => setProductForm({ ...productForm, mixedMaterial1: '' })}
                          >
                            {material.replace('_', ' ')}
                          </OptionCard>
                        ))}
                      </SelectionGrid>
                    </Field>
                    <Field>
                      <Label>{t('frameStockPage.mixedMaterial2')}</Label>
                      <SelectionGrid>
                        {materialOptions.filter(m => m !== 'MIXED').map((material) => (
                          <OptionCard
                            key={material}
                            selected={productForm.mixedMaterial2 === material}
                            onClick={() => setProductForm({ ...productForm, mixedMaterial2: material })}
                            onDoubleClick={() => setProductForm({ ...productForm, mixedMaterial2: '' })}
                          >
                            {material.replace('_', ' ')}
                          </OptionCard>
                        ))}
                      </SelectionGrid>
                    </Field>
                  </>
                )}

                <Field>
                  <Label>{t('frameStockPage.design')}</Label>
                  <SelectionGrid>
                    {designOptions.map((design) => (
                      <OptionCard
                        key={design}
                        selected={productForm.design === design}
                        onClick={() => setProductForm({ ...productForm, design })}
                        onDoubleClick={() => setProductForm({ ...productForm, design: '' })}
                      >
                        {design.replace(/_/g, ' ')}
                      </OptionCard>
                    ))}
                  </SelectionGrid>
                </Field>

                <Field>
                  <Label>{t('frameStockPage.sex')}</Label>
                  <SelectionGrid>
                    {sexOptions.map((sex) => (
                      <OptionCard
                        key={sex}
                        selected={productForm.sex === sex}
                        onClick={() => setProductForm({ ...productForm, sex })}
                        onDoubleClick={() => setProductForm({ ...productForm, sex: '' })}
                      >
                        {sex}
                      </OptionCard>
                    ))}
                  </SelectionGrid>
                </Field>
              </div>
              </FormSectionCard>
              </ModalBody>

              <ModalFooter>
                <FooterHint>{t('frameStockPage.reviewRequiredFieldsBeforeSaving')}</FooterHint>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <Button type="button" variant="secondary" onClick={() => { setShowProductForm(false); resetProductForm(); }}>{t('frameStockPage.cancel')}</Button>
                  <Button type="button" onClick={handleCreateProduct}>{editingProduct ? t('frameStockPage.saveChanges') : t('frameStockPage.createProduct')}</Button>
                </div>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>,
          document.body
        )}
      </Container>
      <CameraCaptureModal open={!!rowCameraFor} onClose={() => setRowCameraFor(null)}
        onCapture={(file) => rowCameraFor && handleRowPhoto(rowCameraFor, file)} />

      {/* Print Modal Overlay */}
      {printItem && ReactDOM.createPortal(
          <PrintModalOverlay onMouseDown={(event) => { if (event.target === event.currentTarget) setPrintItem(null); }}>
            <PrintPreview onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div style={{ textAlign: 'left' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>{t('frameStockPage.qrLabelReady')}</h3>
                  <p style={{ margin: '0.2rem 0 0', color: '#64748b', fontSize: '0.78rem', lineHeight: 1.4 }}>{t('frameStockPage.previewTheLabelAndPrint')}</p>
                </div>
                <button onClick={() => setPrintItem(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>

              <PrintableLabel className="print-label" ref={printLabelRef}>
                <div className="label-inner">
                  {/* Left column: scannable QR */}
                  <div className="qr-section">
                    <QRCode
                      className="qr"
                      value={printItem.frameProduct?.sku || printItem.sku || 'UNKNOWN'}
                      size={256}
                      style={{ width: '14mm', height: '14mm' }}
                    />
                  </div>
                  {/* Right column: product text — name, code, price only */}
                  <div className="text-section">
                    <div className="label-title">{printItem.frameProduct?.name || printItem.name || storeName}</div>
                    <div className="label-code">{printItem.frameProduct?.sku || printItem.sku}</div>
                    {(printItem.frameProduct?.priceDzd ?? printItem.priceDzd) != null
                      && (printItem.frameProduct?.priceDzd ?? printItem.priceDzd) !== '' && (
                      <div className="label-price">{printItem.frameProduct?.priceDzd ?? printItem.priceDzd} {t('frameStockPage.dzd')}</div>
                    )}
                  </div>
                </div>
              </PrintableLabel>

              <Button onClick={handlePrint} style={{ width: '100%', justifyContent: 'center' }}>
                <Printer size={18} />
                {t('frameStockPage.printQrLabel')}
              </Button>
            </PrintPreview>
          </PrintModalOverlay>,
          document.body
      )}

      {/* Create Product Notice Modal */}
      {createNotice.open && ReactDOM.createPortal(
        <NoticeOverlay onClick={closeCreateNotice}>
          <NoticeModal onClick={(e) => e.stopPropagation()}>
            <NoticeHeader>
              {createNotice.type === 'success' ? (
                <CheckCircle size={22} color="#059669" />
              ) : (
                <AlertCircle size={22} color="#dc2626" />
              )}
              <NoticeTitle>{createNotice.type === 'success' ? t('frameStockPage.success') : t('frameStockPage.error')}</NoticeTitle>
            </NoticeHeader>
            <NoticeMessage>{createNotice.message}</NoticeMessage>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="button" onClick={closeCreateNotice}>{t('frameStockPage.ok')}</Button>
            </div>
          </NoticeModal>
        </NoticeOverlay>,
        document.body
      )}
    </>
  );
};

export default FrameStockPage;
